/**
 * The Poetry Room catalog.
 *
 * Poems written by J'Wells, founder of 40 Songs and a Mic.
 * Add new poems to POEMS below. Verses use template literals so line breaks
 * and apostrophes render literally. Empty lines become stanza breaks.
 *
 * Emotions vocabulary (matches the picker filter):
 *   anger, grief, fear, faith, hope, forgiveness,
 *   rebuilding, dark-night, self-worth, love
 */

export const EMOTIONS = [
  { id: 'anger',       label: 'I am angry',            color: 'from-[#E63946] to-[#B22222]' },
  { id: 'grief',       label: 'I am grieving',         color: 'from-[#4B4E6D] to-[#2C2E43]' },
  { id: 'fear',        label: 'I am afraid',           color: 'from-[#3D5A80] to-[#293241]' },
  { id: 'dark-night',  label: 'I am in a dark place',  color: 'from-[#1D1E33] to-[#0a0a0a]' },
  { id: 'faith',       label: 'I need faith',          color: 'from-[#D4AF37] to-[#B8860B]' },
  { id: 'hope',        label: 'I need hope',           color: 'from-[#F4A261] to-[#E76F51]' },
  { id: 'forgiveness', label: 'I need to forgive',     color: 'from-[#8D99AE] to-[#586380]' },
  { id: 'rebuilding',  label: 'I am rebuilding',       color: 'from-[#2A9D8F] to-[#1E7268]' },
  { id: 'self-worth',  label: 'I am worth more',       color: 'from-[#9D4EDD] to-[#5A189A]' },
  { id: 'love',        label: 'I want to love again',  color: 'from-[#E5989B] to-[#B5838D]' },
];

const p = (id, title, emotions, versesRaw) => ({
  id,
  title,
  author: "J'Wells",
  emotions,
  verses: versesRaw.split('\n'),
});

export const POEMS = [
  p('broken-and-contrite', 'Broken and Contrite', ['rebuilding', 'hope', 'self-worth'],
`On broken ground underneath the surface,
Are riches if toiled, bring purpose.
Separating the weak from the mighty,
Unearth the worth hidden in our bodies.
Life beyond flesh and blood,
Light within the soul, gold covered by mud.
I'm sure mining would hurt, if mountains could feel.
Oh, but what precious treasures are found by a drill.
Is it worth the pain, of digging up dirt,
Or don't disturb the ground and end our search?
As for me, I'll dig to the depths of my soul.
To a place untouched by moth or mold.`),

  p('scarred-heart', 'Scarred Heart', ['anger', 'grief', 'dark-night'],
`A scarred heart in my youth, created poison in my roots.
Desensitized by fear, I was afraid of being scared.
I hated my emotions, too broke to be repaired.
Confused by my illusions, abused by my confusion,
I lost contact with my family, the only road to sanity.
Left broken and pitiful, cold-hearted and miserable.
I kept searching for answers, while holding my feelings ransom.
Pride has brought destruction. My heart thrived on corruption.
Soul possessed with anger, placing my life in danger.`),

  p('my-mirror', 'My Mirror', ['grief', 'self-worth', 'dark-night'],
`Sometimes I don't like what I see,
Whether looking in the mirror or inside of me.
The reflection of my life got me stuck in the past.
While living for the moment, I was moving too fast.
Others' influence is what guided my life.
Trying to reach their expectations is what blinded my sight.
Searching for a path that would lead to happiness,
Empty hole in my heart, but my façade was fabulous.`),

  p('life-of-shame', 'Life of Shame', ['grief', 'dark-night', 'fear'],
`A child left to himself causes shame. Clueless, who am I? Molded in pain.
Walk a mile in my shoes. Life's one big party, living by my own rules.
Suppressed emotions caused a violent explosion.
Drown my problems in alcohol, cast in the ocean.
In a sea of misery, deceived by my own notions.
On the island of loneliness, screaming for help.
Yelling desperately before I silently wept.
No answer to my call led to more drinking,
Thinking: 'what are you doing, red lights are blinking?'
Proceed to move forward in the middle of traffic,
Full speed ahead, a collision waiting to happen.`),

  p('the-mask', 'The Mask', ['anger', 'fear', 'self-worth'],
`Insanity ensued the abused dude,
Too confused to tie his own shoes,
So that he's laced up.
His face looks tough,
But, he's just a scared kid using anger as his make-up.
Shake up the whole world when he wakes up the sleeping giant.
Life comes after dying.`),

  p('bred-with-tragedy', 'Bred With Tragedy', ['anger', 'grief', 'dark-night', 'self-worth'],
`If you could see my life as it existed as it unfolded.
If you could witness the twisted facts that it unloaded.
Then you would understand if I exploded.
Molded as a product of my environment severely scolded.
Humiliated like America's Most Hated.
So angry my eyes dilated.
Like I was under the influence of narcotics.
With a fire burning in my chest blemished and spotted.
There was a time I considered it a curse to be me.
The youngest of my momma's boys, the last to be free.
Everybody's pointing fingers laughing.
"Why did this have to happen to me?"
I'm asking 'cause I can't grasp it.
I vowed to tell no one how these bastards trashed my integrity.
I was mad so when I got older I would smash on society.
If only I knew then half of my insanity.
But I denied its existence and wouldn't confront it.
It kept on hissing so I kept on running.
Until it bit me like a poisonous snake.
It was a fate that I couldn't escape.
I had a demon in me screaming for revenge.
I didn't trust nobody I was afraid of making friends.
Puffing on drugs led to an alcoholic binge.
Catapulting me to popping pills and toasting to sin.
Using women to replace what I thought was taken from me.
A recipe for a chase that would keep me hungry.
Falling apart with no one there to help me,
In emotional poverty pretending that I'm wealthy.`),

  p('conscious-reflection-part-1', 'Conscious Reflection — Part I', ['grief', 'forgiveness', 'dark-night'],
`Trapped in the memory of a painful past.
Reliving the misery every so often.
Grief electrocutes my heart suddenly.
I become a prison of mistakes.
Regrets visit me to remind me where I've been.
A lesson not to walk that path again.
I destroyed many with acts of foolishness.
Relationships now lay in ruin.
If only I could turn back the hands of time.
I caused one of God's faithful ones to stumble.
A milestone should be tied around my neck and thrown into the sea repeatedly.
Living in a prison of selfishness.
No one mattered besides me.
Cherishing my own pleasures most.
It's hard to be happy when you caused others to lose sight.
My heart condemned me, disapproving of my selfish ways.
Relive them to see what foolishness pays.
Travel back to the point of being lost.
The point where the line was crossed.
When conviction set in mentality freezes.
Witness an immature state from maturity.
The past can never be undone.
Nor the pain so many have felt.
Yes, death would be easier than living with my memories, but still I live!`),

  p('conscious-reflection-part-2', 'Conscious Reflection — Part II', ['forgiveness', 'faith', 'grief'],
`I've been destroyed by others — a victim to prisoners of selfishness.
It's a contagious disease, infecting all in contact.
Fortunately, I regained consciousness, placed back on track by divine intervention.
I still revisit the days of my innocence.
With child-like understanding without knowledge.
Loving unconditionally even when completely destroyed.
Not knowing how to hate — my heart was still pure.
Loving those who destroyed me.
Until fleshly instincts arose hate — creating friction.
For forgiving instead of retaliating.
Finally I was consumed.
I fought as long as I could.
Vengeance would no longer be silenced.
It became stronger than me.
A blazing inferno, wanting to do what was done to it — collecting a long-lost debt of humiliation.
Wreak havoc — it's destructive by nature.
Especially to those who were affected by my vengeance.
It became the fuel that consumed all morals.
Forcing the mind into an unrealistic state.
Its fate becomes unknown.
Everyone takes advantage of this unknowing condition.
And strengthen its resistance against self.
Yes, death would be easier than living with my memories, but still I live!`),

  p('puppy-love-gone-bad', 'Puppy Love Gone Bad', ['grief', 'forgiveness', 'rebuilding'],
`Puppy love gone bad created the trust issues I had.
Two bad relationships put me on the path of not caring.
A so-called playa for life, I'll never get married, I don't want a wife.
Vowed a woman will never hurt me like that again,
This began shallow, sexual encounters with girlfriends.
Labeled friends with benefits, a by-product of ignorance.
Afraid of getting crushed like when I gave up too much of me.
We broke up and it was misery I couldn't control.
Deep in my soul my emotions were scarred.
Locked them behind prison bars for making me feel,
Banished to administrative segregation without an appeal.
Callous were my relations when facing intimacy,
But now I'm granting them clemency.`),

  p('betrayal-plus-hurt', 'Betrayal + Hurt = Slavery', ['forgiveness', 'grief', 'anger'],
`What's sweet like honey but turns to gravel.
And tears your life apart when it all unravels?
Feeling good for a moment — then it fills with regret.
Reality sets the stage for an emotional upset,
Once released from its spell.
Life's worst kept secret — loyalty failed.
Betrayal at its peak — hearts are crushed.
Severed relationships struck by the blades of lust.
Mending is not possible deep in the core.
Violation of trust — one can seldom restore.
Then the blame shifts to the unforgiving spouse.
Like their unforgiving heart can be torn right out.
They become slaves to betrayal and hurt.
It's out of their control whether apologizing works.
Violated trust leaves deep cutting scars.
Causing one to bleed out all that they are.
Losing oneself is a part of the process.
Lost in a world of hurtfulness.
Revenge takes control and compels them forward.
Delusions take hold — destroying moral boarders.
Waters spilled abroad in the streets.
For immoral strangers to drink.
Response of the emotionally wounded.
A life of tragedy for temporary passion.
Many lives are destroyed from one person's actions.
Such is the consequence of selfishness.
A self-destructing bomb of helplessness.
When it explodes — hearts are slain.
Chaining its victims to a life of pain.
Most seek revenge and pass the hurt on.
Creating a cycle of repeating the wrong.
A trail of broken hearts and crushed emotions.
Victims find fulfillment in ripping others open.
Passing on the pain that was passed to them,
Violating what they once condemned.
Retaliating will leave them empty, bitter inside and filled with envy.`),

  p('written-in-rain', 'Written in Rain', ['faith', 'hope', 'dark-night', 'grief'],
`It's cold and lonely doing time. God was the only friend I could find.
Forsaken by family and so-called friends. Write letters, but no response to my pen.
Stand at my door during mail call, hoping for a reply.
Disappointed as the guard with the mail passes me by. Wondering why?
Maybe I'll get some mail tomorrow. This hope helps me cope with pain and sorrow.
Comfort myself with self-deception. Day after day — it's a form of protection.
From all the pain i'm hurting with. Gather strength to move forward — learning quickly.
As I hunger and thirst for companionship. Everyone's too busy so i'm emotionally indigent.
Whatever happened to unconditional love? Betrayal and anger flow through my blood.
I want to give up but that's not me. No escape so I cope with misery.
Suppressed emotions are explosive material. Combustion, my turmoil turned lyrical.
Musical emissions began to seep. Bleeding emotions caused my pen to leak.
Silently waiting to release pressure on my brain. Under the power of affliction was I trained.
It's only a matter of time until I break these chains,
And reveal to the world what was written in rain.`),

  p('never-out-of-satans-reach', "Never Out of Satan's Reach", ['faith', 'forgiveness', 'self-worth'],
`It seems that I've been riding on my high horse.
Pride has separated me from my source.
So holy I thought I was invincible.
Deceived again, so I abandoned my principles.
Exalting myself above the ways of mankind.
Now I'm faulting myself for exaltation in my mind.
I must remain humble at all times.
Never forgetting I used to walk according to this world — praising Satan when I talked.
Though that part has been separated from my heart.
I still feel its pulse within.
I'm addicted to a dope called sin.
Depending on a drug that I hate to use.
Determined to abuse this same drug so I'm made confused.
I've been torn in two.
Born into a world where I'm destined to lose.
Unless I choose to put Christ first and walk in his shoes.
And stop Satan from igniting my fuse.`),

  p('dear-god', 'Dear God', ['faith', 'forgiveness', 'hope'],
`Dear God, Please forgive me for all the pain I caused.
All I really want is for the hands of time to pause.
Take the time to search my past, see all the hurt involved.
At times I take a glance to see if my life evolved.
Or if I'm trapped in the same cycle, repeating the same actions,
That got me trapped in this place, living life in the same fashion.
Where did I go wrong to bring this hurt to myself?
Bring deep to my heart my flaws and imperfections,
And I will strengthen those parts 'cause I will fall without direction.`),

  p('final-thoughts', 'Final Thoughts — No Xcusez!', ['rebuilding', 'faith', 'forgiveness', 'hope'],
`Our past, beware!
The hurt we bear lies there.
Smoldering coals of unresolved issues — do not enter alone!
The valley of dead bones where skeletons roam.
Unsettled, restless, paranoid victims seek vengeance and retaliation.
Angry and embittered, poison drips from infected wounds.
Consuming common sense and selflessness.
The result: ignorance and selfishness.
Two destructive forces that destroy the worlds between us.
Unless they are reversed and we clean the filthy pearls that became us.
The curse will continue. The change starts with you.`),

  p('the-man-i-was', 'The Man I Was', ['self-worth', 'grief', 'dark-night', 'forgiveness', 'rebuilding'],
`I was selfish, absorbed in myself,
Totally inconsiderate, lacking spiritual health.
Irresponsible is how I would define me,
In bondage to sin, spiritual captivity.
A living catastrophe, I was prone to faults.
Everything I touched was blown apart.
I could do nothing right, everything went wrong,
Like God cursed the ground I was walking on.
Empty, I felt this hole in my soul.
I tried to fill it with drugs, sex, and Rock 'n' Roll.
Came off my binge and I was still empty.
Looking for the next high and girls who were friendly.
Miserable, I couldn't cope with being sober.
Addiction told me I could lean on its shoulders.
Man, I couldn't wait for the pain to be over.
As years went by I got a little bit colder.`),

  p('rolling-the-dice', 'Rolling the Dice', ['fear', 'anger', 'self-worth'],
`Raised under an oppressive dictator called fear.
I'm tired of being scared; explore the dominion I have here.
Shed tears, conflicted with my many beliefs.
Poppa said, 'Men don't cry,' why do my eyes leak?
Confused by this false understanding of being a man,
Too elusive for me to understand.
Settled for the cheap knock-off version,
Sleep with lots of women, immersed in perversion;
Resolve conflicts with fist fights;
And gamble with life, by rolling the dice.`),

  p('lost-in-the-sea-of-himself', 'Lost in the Sea of Himself', ['dark-night', 'grief', 'faith', 'hope', 'rebuilding'],
`Shield hurt by hiding behind emotions,
Buried beneath the dirt at the bottom of the ocean.
A scared kid lost in the sea of himself,
Damaged, wounded, desperately needing help.
Led by blind men he fell into a ditch.
He failed many times but caught the urge to quit.
He and misfortune had a child named success.
A journey of a thousand miles began with one blessed step.
If you could see him now, he's the king of the sea.
Surrender to the greater King abiding in thee.`),

  p('long-time', 'Long Time', ['love', 'grief', 'faith', 'hope', 'forgiveness'],
`We've been through so much, a mother rearing her child.
Not one but eleven, through all the ups and downs.
When I was young I didn't understand the struggles you faced.
We were poor; I couldn't see the pain in your face.
Forced to leave pops because he was an abusive man.
You filed for divorce to give us a better chance.
The road was tough but you took it in.
You taught me the meaning of being strong.
As I got older I could see the pain in your eyes,
From sleepless nights, wondering if I was alive.
The road I was headed down, there was no telling what I would find.
Love covered a multitude of sins I committed in my mind.
Though all have forsaken me, you're still by my side.
The type of tragedy only a mother's love would survive.
When they sentenced me I heard the pain it brought in your life.
Trying to be strong for me, while you were hurting inside.
My pillar that supports me on this long and dark ride.
Keep my eyes on Jesus you told me time after time.
'God has a purpose,' you instilled in my mind.
Your gift to me was great faith in the Divine.
You helped me to see the Light when the sun didn't shine.
Thank you for sticking with me after all this time.`),

  p('walking-weary', 'Walking Weary', ['grief', 'fear', 'faith', 'dark-night'],
`Weary, yet continue to walk.
Shedding tears, stress attacking my thoughts.
Confused, why have I been cast down?
Or so it seems, know not what to ask now.
Clouded thoughts interrupt a sacred vision.
Spiritual discernment, the lack of which causes collision.
The breeding ground of destruction,
For not heeding divine instruction.
Stiff-necks will be broken without remedy.
Relaxed standards, the work of the enemy.
Minimized for the sake of convenience,
Lost in interpretation, and doctrines of demons.
Thou shall not, means just that when interpreted.
Disobedience, the sign of the serpent's kids.
Casting doubt, fishing lines with big hooks.
Tempting bait, hypnotized with one look.
Crooks patiently wait for their lies to be taken.
Our lives are as open books, read before all men.
Judge not, for none are without sin.
Living Bibles, the only kind some will ever read,
Actions are pages, the fruit of our tree.
Accomplishing whatever we believe.
More than conquerors, faith the size of a mustard seed.
Small beginnings, but it finishes big.
Produce works unlike the tree without figs,
Yeshua cursed, and it withered and died.
So will you and I, if we don't abide,
In the sacred Vine, we are the branches.
It's not wise to sever ties, in order to run rampant.
Without Him, we are worthless,
Just facades fading away earth's list.
Playing cards we are dealt.
Deck stacked against us, gambling heavenly wealth.
Squander it all, the human mind is broken.
Now reprobate, confined by false notions.`),

  p('this-is-where-we-learn-again', 'This Is Where We Learn Again', ['faith', 'rebuilding', 'forgiveness', 'hope'],
`I want to be fashioned by His holy hand,
And taught how to live in this wicked land.
Where the lost leads the lost, Yah forbid.
And to forgive, was burnt up by an arsonist.
A world full of hypocrites and broods of vipers,
Prey on the innocent victims of spiritual snipers.
When will the blind see again?
Post it on a bulletin, To those lost in sin;
The Almighty makes us kin, Stop acting like hooligans.
This world is in captivity, under the illusion of being free.
Twisted priorities ignore the unseen.
They can't envision it without a screen.
Twisted priorities ignore the unseen.
Held in slavery, they obeyed the wrong master,
Wounded fatally, in search of green pasture.
Roam through dry and desolate places,
In a pig pen longing to fill their faces, With pig slop.
When they come to their senses,
They go home, a path of repentance.
Our Father's servants are treated better than the world's slaves.
On our journey He will meet us along the way.
Repetitious steps will conquer a thousand miles.
Slow progress leads away from the defiled
Refusing to read makes it hard to grow.
Stuck in the past, we won't let it go.
Watching it like re-runs of a TV show.`),

  p('mr-selfish', 'Mr. Selfish', ['self-worth', 'faith', 'forgiveness'],
`Salt loses flavor when children don't learn their lessons.
Trying to live roles ill-equipped with blessings.
Spiritual amnesia, forgetting what you've done,
How you so loved the world, you gave us your only son.
Like that's not enough, we ask for more.
Unlimited blessings let the heavens pour.
Good health is not enough, we want riches galore,
Overflowing and spilling onto the floor.
Sure, health is good, but it's not a reward.
Wisdom: God's Son died for sins.
Mr. Selfish: Bless me with money.
Wisdom: Children are missing limbs.
Mr. Selfish: I want the finest of honey.
Wisdom: There are children dying from cancer.
Mr. Selfish: Lord, when I pray, why don't you answer?
Wisdom: Women are raped.
Mr. Selfish: Thank you for my new shoes.
Wisdom: The world is full of hate.
Mr. Selfish: I understand their point of view.
Wisdom: All sinners will perish.
Mr. Selfish: Lord please save me.
Wisdom: Your brother's wife had a miscarriage.
Mr. Selfish: Halleluiah, I'm free.
Wisdom: What about the man contemplating suicide, say something quick.
Mr. Selfish: Okay, okay, excuse me sir, I got a problem you can help me with.
The path I'm on don't match what Scripture depicts.
Stranger: God reroutes those who leave something too precious to forget.
Wisdom: Grateful, Mr. Selfish replied.
Mr. Selfish: Death is not the answer, it's a cunning trick.
Wisdom: What did I forget? Mr. Selfish thought,
As destiny sent him down the path he just walked.`),

  p('recycling-characters', 'Recycling Characters', ['grief', 'anger', 'dark-night', 'self-worth', 'rebuilding'],
`Imagine being born fighting for life,
It was a sign that my future would be full of strife.
Pops left home, he was abusive to my mother.
Mom worked two jobs; I was raised by my brothers.
The youngest of eight, I tried to hold my own.
I had a roof over my head but I wouldn't call it home.
Home is where the heart is, mine was far from it.
Turned into a hardhead, is what I got from it.
Emotions weren't valued, I learned to stuff them.
Didn't wear them on my sleeves, I was quick to handcuff them.
Raised under a code that men don't cry,
And anything goes in the streets at night.
Life is a jungle, we live among beasts.
If you're too scared to hunt you'll be turned into meat.
Invited to a feast at your own expense.
This is the road that leads to death,
Where no human soul will ever find rest.
Predators become prey and prey becomes predators,
An ongoing recycling of these characters.
Nothing gets better, it only gets worse.
Temporary fixes for the pain and hurt,
Sex, money, and drugs, Fiends, addicts, and takers.
Raised without love, created a heartbreaker.
In and out of clubs, rubbing on rump-shakers.
Misery loves company, he's now a representative.`),

  p('expecting-life-to-be-a-party', 'Expecting Life to Be a Party', ['grief', 'dark-night', 'faith', 'rebuilding'],
`Pretense, I got it together,
It's summer in all this bad weather.
Icy conditions, no place to call home.
Journeyed wherever passion burned.
Expecting life to be one big party.
Responsibility had no part of me.
No wonder why I was forsaken.
Condemnation, authenticates those who pretend,
Lonely like severed limbs, broken relationships, only He can mend.
I want out of the kitchen, I endured the heat.
Speak to reassure the weak,
Obviously, we're blind to what God can see;
Trust in Him whole-heartedly.
He'll straighten our crooked paths, and rebuild houses made of glass.`),

  p('spiritual-rider', 'Spiritual Rider', ['faith', 'forgiveness', 'self-worth', 'rebuilding'],
`I'm a spiritual rider, gospel advisor.
These days considered an outsider.
What fellowship has light with darkness?
Wear my feelings on my sleeve 'cause I don't want to be heartless.
I was a callous individual, stuck in my ignorant role.
Mastering stupid, 'cause anything goes.
There are no rules in the streets.
Pity the fool putting his trust in me.
I was aimless; pretending to be famous.
In anguish, couldn't even speak my own language.
Reached for heights knowing that it was dangerous.
Loved playing with fire until I got burned, But I was burned before.
Playing with desires closed behind a door.
We all got secrets hiding in the closet, The many mistakes we often deposit.
Some get away and escape detection.
Others are exposed for their fraudulent projections.
At the intersection of fallen and weak,
At the end of our rope when our limits are reached.
Consequences should be considered before, not after an action is taken.
What will this cost me? You should be asking Satan.
Tempted with lust that is fabricated.
Sin crouches at the door desiring to have us.
Like a hungry lion waiting to grab us.
We'll be devoured in seconds while it's wreaking havoc.
Lessons of the foolish our ending is tragic.
Shining for a moment but it's not everlasting.
Costly investments time will tarnish and trash it.
Caught in this madness everyone out for themselves.
Stepping on each other is the meaning of hell.
Just to get ahead, we'll do whatever to prevail.
Our hearts have been hardened, we need to crack its shell.
Awaken our hearts to love and compassion.
Only Torah studies can restore this fashion.
Keep seeking, never cease from asking,
Pleading with our Father through prayer and fasting.`),

  p('long-time-pt-2', 'Long Time — Pt. 2', ['grief', 'forgiveness', 'love'],
`I chose to run the streets like Tupac with his thugs,
While you were waiting at home for my love.
I disrespected you in more ways than I can count,
Never apologizing for bringing different women into your house.
No wonder you hate me, but your love ran deep,
Understanding that I was walking while asleep.
You gave me time to wake up.
I saw the good I had before you finalized our breakup.
Then I was arrested and hauled off to jail.
Sentenced to life in a small cell.
Restless on my bed poring over thoughts.
I left you when you were pregnant, I can't excuse that part.
I burned the candle at both ends, sowed my royal oats.
Days turned to months before I finally awoke.
I realize that I left you when you needed me most.
The damage is done; I can't fix what I broke.`),

  p('evaluation-and-judgments', 'Evaluation and Judgments', ['forgiveness', 'grief', 'self-worth'],
`We be carrying grudges.
Acting like prosecutors and judges.
We judge people's actions.
Convicting them in the courtroom of our minds.
Guilty of transgressing and crossing an invisible line.
Confined behind the bars of our resentment.
On the surface they are free.
In our hearts they received a life sentence.
The real prisoner is me.
My heart became a prison no one can see.
Holding on to past hurts is captivity.
Forgiveness is the only way to escape this penitentiary.`),

  p('spiritually-renovated', 'Spiritually Renovated', ['faith', 'rebuilding', 'hope', 'forgiveness'],
`Struggle and strife, define my conditioning.
It's folly to answer before listening.
Seek counsel before going to war.
Mentally, insanity's knocking at the door,
To vandalize the home, and leave it in ruins.
Homeowner was watching, thinking, what are they doing.
Their presence proved the existence of the opposite.
If demons are real, then God must exist.
They spotted me watching them tear my house apart.
They charged, I didn't fear knowing God had my heart.
What would God do as these demons approached?
A light flashed, and covered me like a coat.
When it faded, the demons, and destruction were gone.
I was elated that God protected His own.
You see, I was the house that was nearly destroyed,
And cleaned up from being spiritually void.
Uncertain of what I was living for.
Every moment I struggled with this war.
Beaten by life.
Force a smile on my sad face.
Don't ask why I hide the pain of crushing weight.
Afraid to admit that the pain I felt,
Made me curse the ground, and the cards I was dealt.
Sufficient are our troubles, one day at a time.
Life is a struggle, balanced between three lines.
Altruism on the right, on the left is egoism.
We balance the two in the middle.
Resemble the attributes of our heavenly Father.
Reap sensation, rewarding spiritual dollars.
Understanding should be valued more than gold.
Correcting our desires is the number one goal.
Reverse the will to receive, to the will to bestow.
The evil inclination, if uncorrected, will grow.
Emptiness, the results of vain pursuits.
Proof, there's no fulfillment in the absence of truth.
Truth, being the reality of the spiritual realm.
Perceived by the sixth sense developed in them.`),

  p('a-just-measure', 'A Just Measure', ['forgiveness', 'faith', 'self-worth'],
`Feel the weight of who I am.
Renewed by the blood of the lamb.
Judge not, lest ye be judged.
Cast the first stone if you're not covered in mud.
A man's righteousness is as filthy rags.
So your righteousness, is only fit to be tossed in the trash.
I'd rather be a thief dying on the cross, saying, 'Remember me,'
Or a sinner beating his chest in misery,
Begging to be forgiven, too embarrassed to look at heaven,
Than a Pharisee saying, bread eaten in secret is pleasant.
A bond servant who seeks to serve,
Not to be served.
Preach the word as a living example,
Embody the Scriptures that carnal feet trample.
Some just want a sample, to put a trophy on the mantle.
The Spirit consumes lives like a candle.
Requesting a crucifixion is too much for some to handle.
Leave behind all, to carry your cross.
On the sacrificial altar, sometimes living sacrifices get off.
Life has a way of testing our relationships.
There's a way that seems right, but causes the foot to slip.
Which leads to being unprofitable,
Like fluids in a handkerchief.`),

  p('the-silent-voice', 'The Silent Voice', ['faith', 'hope', 'dark-night', 'love'],
`Pick up the wreckage now known as my life.
Moving forward in darkness searching for light.
Lost, but in my mind I hear a silent voice.
Decifer spiritual talk before making a choice.
The wrong decision imprison with destruction.
Costly mistakes will take me to nothing.
Humbled, lacked friends who care.
Search the darkness to find no one anywhere.
The world's heart is hardened to its neighbors.
Self-preservation is the first law of nature.
Dog eat dog world, concrete jungles.
Light of hope at the end of the tunnel.
Suffering and pain made many go numb.
Animalistic instincts created moral bums.
Love others as thyself is a costly price.
The royal keys to the crown of life.
Interwoven secretly into this silent law.
Pathway to happiness, spiritual awe.
Elevated heights define revelation.
Priceless insight, the appetite of motivation.
Lesson of life we all must learn,
Obstacles strengthen character and progress burns.`),

  p('new-creation', 'New Creation', ['faith', 'rebuilding', 'forgiveness', 'hope', 'self-worth'],
`Sympathetically, I regret to inform you,
That the man whose body I now possess, is now dead.
He died a long time ago, at the hands of merciless men.
Yah wanted to use this vessel so he sent me,
That the dead may be spiritually free.
To all that enslaved men so easily before.
We called and knocked, he opened the door.
Struggling, not knowing what he was living for.
Seeking refuge from death, he was wise, not trusting in himself.
He submitted willingly to the death of his mind.
For a greater reality, sight given to the blind.
His efforts were rebuked and sent to the altar.
A living sacrifice he became for his Father.
Divided by correction and his will.
The flesh fights reproof, but he's spiritually sealed.
Gave up his life for my sake.
Gained life in return, he will not break.
Careful not to make the wrong turn,
Spiritually exercising, determined to stand firm.
As long as he follows, he won't be left behind,
Zealous for good deeds, one day at a time,
Ashamed before none, witness to all,
Not with words, but works, like Apostle Paul.
To this servant, submitting to his Master's will, was his aim,
Accepting responsibility for living profane,
Not pretending to be deceived, and shifting the blame,
Like woe is me, but do nothing to change.
Godly fear is to be holy reverent,
Equally yoked with the spiritually benevolent.
Don't fellowship with those who walk in darkness.
Those who contaminate the spirit by letting the beast mark it.
Don't walk in the counsel of the ungodly,
The carnal minded stranded in death's lobby.
Remember we're sojourners passing through.
Never be ashamed that Yah's blessing is on you.
Counted among a chosen few,
The old passed away, all things made new.
Living to people's expectations, you will lose yourself.
Yah's expectations lead to heavenly wealth:
To study to show thyself approved,
A workman rightly dividing the truth;
Learn to forgive, though it's not easy;
Love your enemies, give to them freely;
Turn the other cheek by not trading insults;
And live humbly and let Yah exalt.`),

  p('our-measuring-stick', 'Our Measuring Stick', ['faith', 'forgiveness'],
`Measures of faith are tested.
Satan has fashioned his weapons.
The world's condemnation is unrelenting.
Tongues shall not prosper, its projectiles are empty.
Attempting to stop the message in my lungs
From reaching the chosen ones.
A battle not of my choosing; I'm stuck in the middle.
Carrying vaccines to the sick in hospitals (churches).
The enemy hinders their recovery.
Tempting them to take comfort in luxury,
If only they bow down and worship him,
Settling for less than God's workmanship.
He alone shall we worship.
Through him alone was earth built,
And dominion was given to man.
Which was transferred to Satan's hands.
Satan tempts us with what was once ours,
To trade the immortality in these glass jars.
Baptized in blood, which cleanses our garments,
Frees our conscious from eating vomit.
No longer slaves, relationship restored,
All in the name of our Lord.
While we were yet sinners Christ died for us.
Don't trade the eternal for what decays and rust.
Temporary satisfaction, the blades of lust,
Reduced to heaps of ashes and dust.
Your ways are much higher than ours.
Mankind is reckless and leaves permanent scars.
If judged by our own measure, few would survive.
Fierce judgments when dealing with our neighbors' lives.
No mercy, compassion, or forgiveness,
Thirsty fits of anger, condemning others to prison,
For the same actions they committed.
Unmerciful servants refuse to admit it.
We quickly exonerate our own debt.
Those of our debtors we promptly collect.
Victims of the 'poor me' syndrome.
Other peoples sins we view like an addendum.
Judgment awaits unmerciful servants.
Debts reinstated, they didn't deserve it.
Condemned by our own measure.
Greed leads to being victims of endeavors.
Dealing with others in a harsh manner.
No compassion, forgetting we're held to a higher standard.
Silently waiting in the shadow,
While men build on foundations that are shallow.
Ignorant building houses on sand,
After a storm they no longer stand.`),

  p('fighting-goliath', 'Fighting Goliath', ['faith', 'hope', 'self-worth'],
`Trying to clear my thoughts, Father search me.
Open my heart, the world tried to hurt me.
Condemnation, the manifestation of demonization.
I refuse to rest in this tomb of incarceration.
Searching for the even hands of justice.
Over inflated lives must be busted.
Humility can make a prince feel like nothing.
He's still a prince, his attitude readjusted.
Separated from his ego, and what easily entangles.
Casting it aside so the seed isn't strangled.
Not separated by height nor depth;
From the Father's love,
Though cautious of every step.
The Father turns men's souls into a golden lamp stand.
If we hold his hand, and forsake our plans.
His ways are higher than ours; setting paths ablaze.
Lighting visions that amaze.
People perish for lack of knowledge.
Casting off restraints, prep vessels for bondage,
Pursuing vain imaginations,
Slay Goliath, with no hesitation.
Unclean thoughts must be corrected.
The faith is a shield of protection.
Hate sin for the destruction it causes,
Strayed due to skeletons in the closet.
Take heed to instructions, lest you fall.
Choose ye this day who you will serve.
Life or death, determined by His word.`),

  p('ship-wrecked', 'Ship Wrecked', ['love', 'faith', 'hope', 'dark-night'],
`When roses are no longer red, nor violets blue,
I'll still be here with a strong love for you.
Do you know the meaning of unconditional?
It lasts forever, always intentional.
It can be heard, when listened for.
Open up when you hear the knock at the door,
Quiet calls to your confused life.
Silence befalls those struck by strife.
Hope was deferred, so the heart got sick,
Broken into pieces, but not hard to fix.
Satan says it's pointless, beware of his tricks,
To get you unstable, so you mentally shift.
Struggles of life toss about like a ship.
Without control colliding into a cliff.
Ship wrecked, spit upon dry land,
Broken into pieces, washed ashore,
Notice foot prints in the sand.`),

  p('raining-acid', 'Raining Acid', ['faith', 'hope', 'dark-night', 'self-worth'],
`Words like ammunition can cause death.
Cursed is the man who puts trust in the flesh.
Resuscitated from the dead, conscious is resurrected.
Proverbs soothe the restless heart with a message.
Knowledge, sweet like honey, coat the tummy with a lesson.
Powers of perception, the enzymes of digestion.
Consumption of affliction can impede any progression.
Angels intercede on sacred ground for our protection.
Don't pity thee, calamity makes us stronger.
Before honor is humility, vanity is no longer.
Perceive every deed in a way that strengthens the persona.
Live a pleasing life like a sweet smelling aroma.
Spiritual hearts of stone, prep man for his surgery.
Heart transplant, in a state of emergency.
Waste no time with foolish debates.
Like a dying child the world is passing away.
Tear stained cheeks increase with more knowledge.
Like a journey on feet exposed to high mileage.
Unquenchable thirst, works to weaken its hosts.
The heat of the dry desert reaching into their throats.
Hunger pains attack like a wise serpent.
A burning rain laid the ax to his surface.
It's Raining Acid, he wish it would stop.
In the distance, he sees Honey from a Rock.`),

  p('a-deeper-thickened-love', 'A Deeper Thickened Love', ['love', 'forgiveness', 'self-worth'],
`Will a deeper thickened love be developed in me,
Where I learn to see people through the love in me.
Not the wickedness awakened by the poisonous tree.
Which severed the connection between my neighbor and me.
Nullify myself before the people I meet.
Alleviates conflicts so we can live in harmony.
The clash of our ego, assertion of our will.
I feel what is hidden to most.
Cloaked in a coat perceived as reality.
What does that mean?
There is the unseen and the coat is the screen.
Like living in a dream, the imagination is king.
False pleasures appearing real spring up like fountains,
Increase gaps between mountains.
Known as valleys laden with death.
Death being separation from our help.
Our creator. The reason Yeshua wept.
We're too distracted to follow the Shepherd's steps.
So we're lost sheep groping in darkness.
More laborers are ready for the harvest.
The wheat is gathered but the tares are discarded.
Tied in bundles prepared for fire.
Such is the fate for failing to cultivate faith.
Choosing evil instead the salt has lost its taste.
We are all given a measure we must plant in its proper place.
Surround myself with givers not takers,
Builders not breakers.
Avoid the destroyer of souls.
Chasing gold, they gain the world but crack the mold.
Forgot the goal physical treasures allude to spiritual attributes.
Confused in our pursuits blinded to this truth.
Greed fueled by temporary satisfaction.
Mislead fools spend their life grasping what turns to ashes.
Instead of treasures that are everlasting.
Peace, love, joy, longsuffering, kindness, justice, and humility.
Take years to develop.
Each encounter must be carefully measured.
Using a fair standard my perception creates the balance.
The weights in my bag should be just towards others.
Instead of painting a picture with unflattering colors.
A reflection of which is only of the colors I mixed.
To capture the figure I'm trying to depict.
So they are perceived in the manner that I envision.
Through the bitterness of my inner prison.
Projecting my ugliness onto the portraits I paint.
Claiming it's them but it ain't.
Really, it's the unclean part speaking from the abundance of my heart.
Distorted perceptions lead to unjust measures.
Thus, one man sees trash while another sees treasure.
The wise learn to die but the fool seeks out his pleasures.
Lets learn a lesson from the weather.
There are four seasons which represents our emotions.
All influenced by our soul, the inner ocean.
Happiness is described as a sunny day with a gentle breeze.
Misery is intense heat at 123 degrees.
Refreshing is a light rain that evaporates as it hits your shirt.
Depressing is torrential rain falling too quickly to be absorbed by the dirt.
It causes mudslides and hazardous conditions.
Houses built on sand will start shifting.
Thus, it has been written of such who clutch the forbidden.
Not repenting of their position.
The wrong are smitten by their own ways.
Life is like jumping out of airplanes.
So what's the use of a good excuse for not having your parachute?`),

  p('condemned-by-the-world', 'Condemned by the World', ['faith', 'hope', 'dark-night', 'forgiveness'],
`Twists and turns along life's highway.
Affliction burns even those who don't stray.
Shattered comfort, attacked by their worst fear.
Darkness covers the entire atmosphere.
Expect the unexpected is a golden rule.
Being alert is a valuable tool.
Mischief creeps like a thief in the night.
Gnashing of teeth when vipers strike.
Clothed in strength, not the physical type,
The spiritual kind, where faith is might.
Journey through the darkness of a man's mind,
To a world not seen with human eyes.
Fed by the hand of the Creator and Master.
Lead to a land rich with green pasture.
Trees planted by rivers don't lose a leaf.
No matter the season, they're attached to the tree.
In a place you can run and not get weary,
And I could speak from the heart and your mind would hear me.
With man it's impossible, but not with God.
Faith the size of a mustard seed beats the odds.
Failing to dream, you'll fade like blades of grass.
Cards we are dealt, was not a man-made task.
Sin interrupted God's divine plan.
Forbidden fruit spread death to woman and man;
Immaturity, confusion, and lack of purpose.
Some get the cure and peddle it like merchants.
Condemned by the world when I played what I had.
Friends can be dangerous, like snakes in the grass.
Venomous bites are fatally inflicted.
Spiritual medication must be taken for the sickness.
Choose not to hate the one who takes from your home.
Just wait and they will reap what they have sown.
Heaven and earth will pass away, but not God's Word.
Sustaining us with nourishment, though hope be deferred.`),

  p('pride-pride-pride', 'Pride, Pride, Pride', ['self-worth', 'forgiveness', 'faith'],
`Unaware are the victims of pride,
Until disasters, and reality collide.
Is another chance a part of your plan?
Patient with the shortcomings of man.
Careful should be those, who think they are standing,
On sacred ground while breaking commandments.
Sorrow takes root in the heart,
For what seems to be defeat for missing the mark.
Stumbling blocks, obstacles that hinder our faith,
Should strengthen His flock, not cast them away.
Occupied with vain imaginations.
Keeping busy, spiritual procrastination.
I'll study harder tomorrow, maybe.
Lack meat, drinking milk like babies.
Grow up, start feeding on substance.
Nourish the spirit, stop chasing after nothing.
Cease from childish arguments and foolish discussions.
Wasting time, idle minds get attacked by lusting.
Prodigal children squander heavenly wealth.
Taking for granted their spiritual health.
Traded for temporary satisfaction.
Moments of egotistical attractions.
Heavenly treasures are everlasting.
Spiritual intoxication, high on compassion.
Divine attributes instilled by our architect,
Release natural chemicals with no side effects.
Learn to be dependent on these chemicals,
Exonerating spiritual criminals.
Released from detention,
No longer blinded by lustful hunger,
Become a sweet smelling aroma.`),

  p('help-me-not-to-take-offense', 'Help Me Not to Take Offense', ['forgiveness', 'love', 'anger', 'self-worth'],
`My neighbor is causing ruckus.
Waking me up early in the morning.
I wanted to ask him what are you doing?
Could you not have found a better time than while I was asleep?
Obviously he intended to cause me discomfort.
I was literally contemplating on returning the insult.
A thought occurred to me that I should pray away the hurt,
So I don't take it personally.
And allow weeds to start growing in this body made of water and dirt.
When transgressed against by men,
I turn cold when the offense sets.
Don't speak to me or look in my direction.
You are dead to me; you get nothing but neglect.
Then my heart speaks and tells me that's not the way to be.
As a follower of Christ I should follow His lead.
What would He do if He was me?
He would see what fueled this misdeed,
And pray for the person who was unable to speak to me.
Does sweet and bitter water flow from the same spring?
Our mouth shouldn't speak bitter and sweet things.
Even if I think about harming my brother,
I'm guilty of murder in the first degree.
This sinful nature has plagued me like Apostle Paul.
The good that I wish to do alludes me,
I'm spiritually confused.
This is where resentment takes root.
I'm sentenced to a condition of constantly switching shoes.
I have been poisoned by bitterness.
My ego fans these toxic fumes.
Making room for humility, the point where strife ceases.
When I'm not insisting upon my own,
I become a servant of the Throne.
Spit in a dead man's face and he won't be offended.
As a living sacrifice, so too, have our lives ended.`),
];

/**
 * Returns the poem for the current ISO week of the year.
 * Rotates weekly and is deterministic per week (all visitors see the same one).
 */
export function getPoemOfTheWeek(now = new Date()) {
  const target = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((target - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return POEMS[week % POEMS.length];
}

export function getPoemsByEmotion(emotionId) {
  return POEMS.filter((p) => p.emotions.includes(emotionId));
}
